import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { monitoring } from 'common/services';
import { isE2ESession, isProduction, isStaffEmail } from 'common/utils';
import { addAppSuccessMessage } from 'modules/alerts/state';
import { isApiResponse } from 'modules/api/utils';
import { AppRoutes } from 'modules/router';
import { push } from 'redux-first-history';
import { handleQueryError } from 'store/utils';

import { userApi } from './userApi';

import type { Effect, RootState } from 'store';

export const userApiListener = createListenerMiddleware<RootState>();

userApiListener.startListening({
  matcher: isAnyOf(
    userApi.endpoints.getAccount.matchRejected,
    userApi.endpoints.updateAccount.matchRejected,
    userApi.endpoints.updateAccountAvatar.matchRejected,
    userApi.endpoints.deleteAccountAvatar.matchRejected,
    userApi.endpoints.updateProfile.matchRejected,
    userApi.endpoints.updateMfa.matchRejected
  ),
  effect: (({ type, payload }, { dispatch }) => {
    const response = isApiResponse(payload) ? payload : undefined;
    handleQueryError(payload, dispatch, type, response?.data || 'Unable to save account information');
  }) satisfies Effect,
});

userApiListener.startListening({
  matcher: userApi.endpoints.getAccount.matchFulfilled,
  effect: ({ payload: account }) => {
    if (account.id === null) {
      Sentry.setUser(null);
      monitoring.clearUser();
    } else {
      Sentry.setUser({
        id: `AID:${account.id?.toString()}`,
        ...(isProduction() ? {} : { email: account.email }),
      });
      monitoring.setUser({
        id: account.id?.toString(),
        // Boolean flag (never the email) so internal traffic can be excluded from RUM/Logs
        // analytics — safe in prod, where account emails are intentionally not forwarded.
        isStaff: isStaffEmail(account.email),
        ...(isProduction() ? {} : { email: account.email }),
      });
      if (isE2ESession()) {
        Sentry.setTag('isE2E', 'true');
        monitoring.setUserProperty('isE2E', true);
      }
    }
  },
});

userApiListener.startListening({
  matcher: userApi.endpoints.updateProfile.matchFulfilled,
  effect: (_, { dispatch }) => {
    dispatch(userApi.util.invalidateTags(['ACCOUNT']));
    dispatch(addAppSuccessMessage('Your information has been saved'));

    dispatch(push(AppRoutes.Account));
  },
});

userApiListener.startListening({
  matcher: userApi.endpoints.updateMfa.matchFulfilled,
  effect: ({ meta }, { dispatch }) => {
    if (!meta.arg.originalArgs.mfaEnabled) {
      dispatch(addAppSuccessMessage('Your information has been saved'));
    }
  },
});

userApiListener.startListening({
  matcher: userApi.endpoints.checkIdp.matchRejected,
  effect: ({ type, payload }, { dispatch }) => {
    const response = isApiResponse(payload) ? payload : undefined;
    handleQueryError(payload, dispatch, type, response?.data || 'Unable to validate email');
  },
});
